import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
  FiTrendingUp, FiUsers, FiDollarSign, FiActivity,
  FiCalendar, FiAward, FiTarget, FiCheckCircle,
  FiArrowUp, FiArrowDown, FiMinus
} from 'react-icons/fi';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './Analytics.css';

const Analytics = ({ currentUser, clients, clientFeedback, workoutPrograms, calendarEntries }) => {
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month'); // 'week', 'month', 'quarter', 'year'
  const [clientGrowthData, setClientGrowthData] = useState([]);
  const [weeklyActivityData, setWeeklyActivityData] = useState([]);
  const [programPerformanceData, setProgramPerformanceData] = useState([]);
  const [satisfactionData, setSatisfactionData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);

  // Calculate metrics
  const calculateMetrics = () => {
    const totalClients = clients.length;
    const activeClients = clients.filter(c => c.status === 'active').length;
    const retentionRate = totalClients > 0 ? ((activeClients / totalClients) * 100).toFixed(1) : 0;

    const totalPrograms = workoutPrograms.length;
    const assignedPrograms = workoutPrograms.filter(p => p.assignedClients?.length > 0).length;

    const averageRating = clientFeedback.length > 0
      ? (clientFeedback.reduce((sum, f) => sum + (f.rating || 0), 0) / clientFeedback.length).toFixed(1)
      : 0;

    const totalSessions = calendarEntries.length;
    const completedSessions = calendarEntries.filter(e => e.status === 'completed').length;
    const sessionCompletionRate = totalSessions > 0 ? ((completedSessions / totalSessions) * 100).toFixed(1) : 0;

    return {
      totalClients,
      activeClients,
      retentionRate,
      totalPrograms,
      assignedPrograms,
      averageRating,
      totalSessions,
      sessionCompletionRate,
      monthlyRevenue: 0, // TODO: Calculate from subscriptions
      revenueGrowth: 0
    };
  };

  const metrics = calculateMetrics();

  // Fetch and calculate real client growth data from Firestore
  useEffect(() => {
    const fetchClientGrowthData = async () => {
      if (!currentUser) return;

      try {
        const usersRef = collection(db, 'users');
        const clientsQuery = query(
          usersRef,
          where('trainerId', '==', currentUser.uid),
          where('role', '==', 'client'),
          orderBy('createdAt', 'asc')
        );

        const snapshot = await getDocs(clientsQuery);
        const clientsByMonth = {};

        snapshot.forEach(doc => {
          const data = doc.data();
          const date = data.createdAt?.toDate() || new Date();
          const monthYear = `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;

          if (!clientsByMonth[monthYear]) {
            clientsByMonth[monthYear] = { total: 0, active: 0, new: 0 };
          }
          clientsByMonth[monthYear].total++;
          clientsByMonth[monthYear].new++;
          if (data.isActive) clientsByMonth[monthYear].active++;
        });

        // Get last 6 months
        const growthData = [];
        const now = new Date();
        for (let i = 5; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthYear = `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
          const month = date.toLocaleString('en-US', { month: 'short' });

          growthData.push({
            month,
            clients: clientsByMonth[monthYear]?.total || 0,
            active: clientsByMonth[monthYear]?.active || 0,
            new: clientsByMonth[monthYear]?.new || 0
          });
        }

        setClientGrowthData(growthData);
      } catch (error) {
        console.error('Error fetching client growth data:', error);
        setClientGrowthData([]);
      }
    };

    fetchClientGrowthData();
  }, [currentUser, clients]);

  // Calculate session distribution from real data
  const sessionDistribution = [
    { name: 'Completed', value: parseInt(metrics.sessionCompletionRate) || 0, color: '#10b981' },
    { name: 'Scheduled', value: 100 - (parseInt(metrics.sessionCompletionRate) || 0), color: '#3b82f6' }
  ];

  // Calculate program performance from real data
  useEffect(() => {
    const programPerf = workoutPrograms.slice(0, 5).map(program => ({
      name: program.name?.substring(0, 15) || 'Program',
      clients: program.assignedClients?.length || 0,
      completion: 0 // Real completion tracking would need additional data structure
    }));
    setProgramPerformanceData(programPerf);
  }, [workoutPrograms]);

  // Calculate weekly activity from calendar entries
  useEffect(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const activityByDay = {};

    // Initialize days
    days.forEach(day => {
      activityByDay[day] = { sessions: 0, workouts: 0, messages: 0 };
    });

    // Get this week's entries
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
    startOfWeek.setHours(0, 0, 0, 0);

    calendarEntries.forEach(entry => {
      const entryDate = entry.date instanceof Date ? entry.date : entry.date?.toDate();
      if (entryDate && entryDate >= startOfWeek) {
        const dayName = entryDate.toLocaleDateString('en-US', { weekday: 'short' });
        if (activityByDay[dayName]) {
          if (entry.type === 'session') activityByDay[dayName].sessions++;
          if (entry.type === 'workout') activityByDay[dayName].workouts++;
        }
      }
    });

    const weeklyData = days.map(day => ({
      day,
      sessions: activityByDay[day].sessions,
      workouts: activityByDay[day].workouts,
      messages: activityByDay[day].messages
    }));

    setWeeklyActivityData(weeklyData);
  }, [calendarEntries]);

  // Calculate satisfaction breakdown from real feedback
  useEffect(() => {
    if (clientFeedback.length === 0) {
      setSatisfactionData([
        { category: 'Training', score: 0 },
        { category: 'Communication', score: 0 },
        { category: 'Availability', score: 0 },
        { category: 'Results', score: 0 },
        { category: 'Knowledge', score: 0 }
      ]);
      return;
    }

    // Calculate average rating as a baseline (out of 5, convert to 100)
    const avgRating = clientFeedback.reduce((sum, f) => sum + (f.rating || 0), 0) / clientFeedback.length;
    const baseScore = (avgRating / 5) * 100;

    // For now, use the average rating as baseline for all categories
    // In a real app, you'd have separate ratings for each category
    setSatisfactionData([
      { category: 'Training', score: Math.round(baseScore) },
      { category: 'Communication', score: Math.round(baseScore) },
      { category: 'Availability', score: Math.round(baseScore) },
      { category: 'Results', score: Math.round(baseScore) },
      { category: 'Knowledge', score: Math.round(baseScore) }
    ]);
  }, [clientFeedback]);

  // Revenue data - would need subscription/payment data from Firestore
  // For now, leave empty until payment tracking is implemented
  useEffect(() => {
    setRevenueData([]);
    setLoading(false);
  }, []);

  const MetricCard = ({ icon: Icon, title, value, change, changeType, subtitle, color }) => (
    <div className={`metric-card ${color}`}>
      <div className="metric-header">
        <div className="metric-icon">
          <Icon />
        </div>
        <div className={`metric-change ${changeType}`}>
          {changeType === 'up' && <FiArrowUp />}
          {changeType === 'down' && <FiArrowDown />}
          {changeType === 'neutral' && <FiMinus />}
          <span>{change}</span>
        </div>
      </div>
      <div className="metric-content">
        <h3>{value}</h3>
        <p className="metric-title">{title}</p>
        {subtitle && <p className="metric-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  const ChartCard = ({ title, description, children }) => (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3>{title}</h3>
          {description && <p className="chart-description">{description}</p>}
        </div>
      </div>
      <div className="chart-content">
        {children}
      </div>
    </div>
  );

  if (loading) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div>
          <h1>Analytics Dashboard</h1>
          <p className="analytics-subtitle">Track your coaching performance and client progress</p>
        </div>
        <div className="period-selector">
          <button
            className={selectedPeriod === 'week' ? 'active' : ''}
            onClick={() => setSelectedPeriod('week')}
          >
            Week
          </button>
          <button
            className={selectedPeriod === 'month' ? 'active' : ''}
            onClick={() => setSelectedPeriod('month')}
          >
            Month
          </button>
          <button
            className={selectedPeriod === 'quarter' ? 'active' : ''}
            onClick={() => setSelectedPeriod('quarter')}
          >
            Quarter
          </button>
          <button
            className={selectedPeriod === 'year' ? 'active' : ''}
            onClick={() => setSelectedPeriod('year')}
          >
            Year
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <MetricCard
          icon={FiUsers}
          title="Total Clients"
          value={metrics.totalClients}
          change={`${metrics.activeClients} active`}
          changeType="neutral"
          subtitle={`${metrics.totalClients - metrics.activeClients} inactive`}
          color="blue"
        />
        <MetricCard
          icon={FiDollarSign}
          title="Monthly Revenue"
          value={`R${metrics.monthlyRevenue.toLocaleString()}`}
          change="N/A"
          changeType="neutral"
          subtitle="Payment tracking coming soon"
          color="green"
        />
        <MetricCard
          icon={FiAward}
          title="Client Satisfaction"
          value={metrics.averageRating > 0 ? `${metrics.averageRating}/5.0` : 'N/A'}
          change={clientFeedback.length > 0 ? `${clientFeedback.length}` : '0'}
          changeType={clientFeedback.length > 0 ? 'up' : 'neutral'}
          subtitle={`${clientFeedback.length} reviews`}
          color="purple"
        />
        <MetricCard
          icon={FiTarget}
          title="Retention Rate"
          value={`${metrics.retentionRate}%`}
          change={`${metrics.activeClients}/${metrics.totalClients}`}
          changeType={metrics.retentionRate >= 80 ? 'up' : metrics.retentionRate >= 50 ? 'neutral' : 'down'}
          subtitle="Active vs total"
          color="orange"
        />
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Client Growth Chart */}
        <ChartCard
          title="Client Growth"
          description="Track your client base growth over time"
        >
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={clientGrowthData}>
              <defs>
                <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2d" />
              <XAxis dataKey="month" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="clients"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorClients)"
                name="Total Clients"
              />
              <Area
                type="monotone"
                dataKey="active"
                stroke="#10b981"
                fillOpacity={1}
                fill="url(#colorActive)"
                name="Active Clients"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Revenue Trend Chart - Hidden until payment tracking is implemented */}
        {revenueData.length > 0 && (
          <ChartCard
            title="Revenue & Profit"
            description="Monthly revenue, expenses, and profit margin"
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2d" />
                <XAxis dataKey="month" stroke="#888" />
                <YAxis stroke="#888" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  name="Revenue"
                />
                <Line
                  type="monotone"
                  dataKey="expenses"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ fill: '#ef4444', r: 4 }}
                  name="Expenses"
                />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  name="Profit"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Weekly Activity Chart */}
        <ChartCard
          title="Weekly Activity"
          description="Sessions and workouts scheduled this week"
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weeklyActivityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2d" />
              <XAxis dataKey="day" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="sessions" fill="#3b82f6" name="Sessions" radius={[8, 8, 0, 0]} />
              <Bar dataKey="workouts" fill="#10b981" name="Workouts" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Session Distribution Pie Chart */}
        <ChartCard
          title="Session Distribution"
          description="Completed vs scheduled sessions"
        >
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sessionDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {sessionDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Program Performance Chart */}
        {programPerformanceData.length > 0 && (
          <ChartCard
            title="Program Performance"
            description="Client enrollment per program"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={programPerformanceData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2d" />
                <XAxis type="number" stroke="#888" />
                <YAxis dataKey="name" type="category" stroke="#888" width={100} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="clients" fill="#3b82f6" name="Enrolled Clients" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Client Satisfaction Radar */}
        {clientFeedback.length > 0 && (
          <ChartCard
            title="Client Satisfaction Breakdown"
            description="Performance across different coaching areas"
          >
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={satisfactionData}>
                <PolarGrid stroke="#2d2d2d" />
                <PolarAngleAxis dataKey="category" stroke="#888" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="#888" />
                <Radar
                  name="Score"
                  dataKey="score"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.6}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Bottom Stats */}
      <div className="bottom-stats">
        <div className="stat-item">
          <div className="stat-icon">
            <FiActivity />
          </div>
          <div className="stat-details">
            <h4>{metrics.totalPrograms}</h4>
            <p>Total Programs</p>
            <span className="stat-meta">{metrics.assignedPrograms} assigned</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon">
            <FiCalendar />
          </div>
          <div className="stat-details">
            <h4>{metrics.totalSessions}</h4>
            <p>Total Sessions</p>
            <span className="stat-meta">{metrics.sessionCompletionRate}% completion rate</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-details">
            <h4>{metrics.activeClients}</h4>
            <p>Active Clients</p>
            <span className="stat-meta">out of {metrics.totalClients} total</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon">
            <FiTrendingUp />
          </div>
          <div className="stat-details">
            <h4>{metrics.totalClients > 0 ? 'Active' : 'No Data'}</h4>
            <p>Growth Tracking</p>
            <span className="stat-meta">Available with more data</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
